import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { faCircleNotch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { NgxFileDropComponent, NgxFileDropEntry } from 'ngx-file-drop';
import { Video } from 'src/app/interfaces/video/video';
import { AuthService } from 'src/app/services/auth.service';
import { ToastService } from 'src/app/services/toast.service';
import { VideoService } from 'src/app/services/video.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-replace-video',
  templateUrl: './replace-video.component.html',
  styleUrls: ['./replace-video.component.scss'],
})
export class ReplaceVideoComponent implements OnInit {
  @ViewChild('fileDrop') fileDrop: NgxFileDropComponent | undefined;

  @Input() selfModal: NgbModalRef | null = null;
  @Input() video: Video | any;
  @Output() replaced: EventEmitter<Video> = new EventEmitter();

  faTimes = faTimes;
  faCircleNorch = faCircleNotch;

  fileName: string = '';
  uploading = false;
  uploaded = false;
  uploadingProgress = 0;
  progressInterval: any | null = null;

  acceptFileUpload =
    'video/mp4v-es,.m4v,' +
    'video/mp4,.mp4,' +
    'video/x-m4v,.m4v,' +
    'video/quicktime,.mov,.qt,' +
    'video/x-quicktime,.mov,.qt,' +
    'image/mov,' +
    'audio/aiff,' +
    'audio/x-midi,' +
    'audio/x-wav,' +
    'video/avi,' +
    'video/msvideo,.avi,' +
    'video/x-msvideo,.avi,' +
    'image/avi,' +
    'video/xmpg2,' +
    'application/x-troff-msvideo,' +
    'audio/avi,' +
    'video/x-matroska,.mkv,' +
    'video/x-ms-wmv,.wmv';

  constructor(
    private auth: AuthService,
    private toastr: ToastService,
    private translate: TranslateService,
    private videoService: VideoService
  ) {}

  ngOnInit(): void {}

  isAdmin(): boolean {
    return this.auth.hasAnyRole(['moderator', 'super_user']);
  }

  isInvalidFileName(name: string): boolean {
    const regex = /[<>:"\/\\|?*\x00-\x1F%&$#]/;
    return regex.test(name);
  }

  openFileSelector($event: MouseEvent) {
    this.fileDrop?.openFileSelector($event);
  }

  fileDropped(files: NgxFileDropEntry[]) {
    files.forEach((dfile) => {
      if (!dfile.fileEntry.isFile) return;
      const fileEntry = dfile.fileEntry as any;

      if (this.isInvalidFileName(dfile.fileEntry.name)) {
        this.toastr.showError(this.translate.instant('video:upload:error:file:name'));
        return;
      }

      this.fileName = dfile.fileEntry.name;

      fileEntry.file((file: File) => {
        let unit = 'MB';
        let maxSize = environment.videoMaxSize;
        if (this.isAdmin()) {
          unit = 'GB';
          maxSize = environment.videoMaxSizeAdmin;
        }
        if (file.size > maxSize * 1024 * 1024) {
          this.toastr.showError(
            this.translate.instant('video:upload:info2', { size: maxSize, unity: unit })
          );
          return;
        }

        let fileCheck: string | null = null;
        if (file && file.type) {
          fileCheck = file.type;
        }
        if (file && !fileCheck && file.name) {
          const ext = file.name.slice((file.name.lastIndexOf('.') - 1 >>> 0) + 2);
          if (ext) {
            fileCheck = '.' + ext;
          }
        }

        const allowedTypes = this.acceptFileUpload.split(',');
        if (allowedTypes.indexOf(fileCheck!) == -1) {
          this.toastr.showError(this.translate.instant('video:upload:info1'));
          return;
        }

        this.checkHasVideo(file).then((hasVideo) => {
          if (!hasVideo) {
            this.toastr.showError(this.translate.instant('video:upload:error:no:video:track'));
            return;
          }
          this.startUpload(file);
        });
      });

      return;
    });
  }

  startUpload(file: File) {
    this.uploaded = false;
    this.startUploadingProgress();
    this.videoService
      .replaceSource(file, this.video.id)
      .then((updated) => {
        setTimeout(() => {
          this.stopUploadingProgress();
          this.uploaded = true;
          this.uploading = false;
          this.toastr.showSuccess(
            this.translate.instant('video:replace:success', { fileName: file.name })
          );
          this.replaced.emit(updated);
        }, 1500);
      })
      .catch((err: any) => {
        this.stopUploadingProgress();
        this.uploaded = false;
        const msg =
          err?.error?.message?.friendly ||
          err?.error?.message ||
          err?.message ||
          this.translate.instant('video:replace:error');
        this.toastr.showError(msg);
      });
  }

  async checkHasVideo(file: File): Promise<boolean> {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      const fileURL = URL.createObjectURL(file);
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(fileURL);
        resolve(video.videoWidth > 0 && video.videoHeight > 0);
      };
      video.onerror = () => {
        URL.revokeObjectURL(fileURL);
        resolve(false);
      };
      video.src = fileURL;
    });
  }

  startUploadingProgress() {
    this.uploading = true;
    this.uploadingProgress = 1;
    this.progressInterval = setInterval(() => {
      const prog = Math.round(1 * (100 - this.uploadingProgress)) / 100;
      this.uploadingProgress = Math.round((this.uploadingProgress + prog) * 100) / 100;
    }, 200);
  }

  stopUploadingProgress() {
    this.uploading = false;
    clearInterval(this.progressInterval);
  }

  closeModal() {
    if (this.uploading) {
      this.toastr.showWarning(this.translate.instant('video:replace:in:progress'));
      return;
    }
    if (this.selfModal) this.selfModal.close();
  }
}
